/** The part of a mutation (`useMutation`) that {@link mutateThen} starts. */
export interface StartableMutation<TData, TVariables> {
  /** Starts the mutation; the promise settles with its result or its error. */
  readonly mutateAsync: (variables: TVariables) => Promise<TData>;
}

/** What follows one call of a mutation. */
export interface MutationFollowUp<TData> {
  /** Runs after the mutation succeeded, with its result. */
  readonly onSuccess?: (data: TData) => void;
  /**
   * Runs after the mutation failed, with its error, for what explains the failure beyond the
   * `error` of the mutation – for example an announcement after the list reloaded (F-61).
   */
  readonly onError?: (error: unknown) => void;
  /** Runs after the mutation succeeded or failed, after `onSuccess` or `onError`. */
  readonly onSettled?: () => void;
}

/**
 * Starts a mutation and runs what follows it once it settles, also when the calling component has
 * unmounted by then (F-63). TanStack Query drops the callbacks passed to `mutate` as soon as the
 * component that called it unmounts – for example a list entry that leaves the list because the
 * mutation refreshed it, or a form whose section re-renders – so an announcement, a focus move or a
 * callback of the parent would be lost. Use this instead of passing callbacks or options to
 * `mutate` or `mutateAsync` (`mutateThen.test.tsx` checks every source file). What acts beyond the
 * component – closing its form in the parent, navigating, a download or another mutation – goes
 * into the guard of {@link useWhileShown}, so it does not run once the component is gone. A failure
 * stays visible through the `error` of the mutation, as with `mutate`; an error thrown by a
 * follow-up reaches the global error handler.
 *
 * @param mutation - The mutation.
 * @param variables - The variables of this call.
 * @param followUp - What runs when the call settles.
 */
export function mutateThen<TData, TVariables>(
  mutation: StartableMutation<TData, TVariables>,
  variables: NoInfer<TVariables>,
  followUp: MutationFollowUp<NoInfer<TData>>,
): void {
  const succeeded = mutation.mutateAsync(variables).then(
    (data) => {
      followUp.onSuccess?.(data);
    },
    (error: unknown) => {
      followUp.onError?.(error);
    },
  );
  void succeeded.finally(() => {
    followUp.onSettled?.();
  });
}
